// src/pages/HomePage.js - React Router対応版
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { newsData } from '../data/newsData';
import { adData } from '../data/adData';
import { heroSliderData } from '../data/heroSliderData';
import HeroSlider from '../components/HeroSlider';
import AnimatedSection from '../components/AnimatedSection';
import { 
  COLORS, 
  FONT_SIZES, 
  FONT_WEIGHTS, 
  SPACING, 
  BORDER_RADIUS, 
  SHADOWS, 
  TRANSITIONS,
  LAYOUT,
  COMPONENTS,
  MOBILE_STYLES 
} from '../styles/constants';
import { applyCardHover, removeCardHover, applyAdItemHover, removeAdItemHover } from '../utils/hoverEffects';

const HomePage = () => {
  const navigate = useNavigate();

  const styles = {
    newsSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    container: LAYOUT.container,
    sectionTitle: {
      fontSize: FONT_SIZES.title,
      fontWeight: FONT_WEIGHTS.bold,
      textAlign: 'center',
      marginBottom: SPACING.xxxl,
      color: COLORS.textPrimary,
    },
    newsList: {},
    newsItem: {
      display: 'flex',
      alignItems: 'center',
      padding: `${SPACING.md} ${SPACING.lg}`,
      borderBottom: `1px solid ${COLORS.backgroundTertiary}`,
      gap: SPACING.md,
      cursor: 'pointer',
      transition: TRANSITIONS.base,
    },
    newsDate: {
      color: COLORS.textTertiary,
      fontSize: FONT_SIZES.sm,
      minWidth: '100px',
    },
    newsCategory: {
      backgroundColor: COLORS.categoryBackground,
      color: COLORS.categoryText,
      padding: `4px ${SPACING.sm}`,
      borderRadius: BORDER_RADIUS.sm,
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.bold,
      width: '120px',
    },
    newsTitle: {
      color: COLORS.textPrimary,
      fontSize: FONT_SIZES.base,
      flex: 1,
      textAlign: 'left',
    },
    newsButtonContainer: {
      textAlign: 'center',
      marginTop: SPACING.lg,
    },
    newsButton: {
      ...COMPONENTS.button.base,
      ...COMPONENTS.button.secondary,
    },
    workSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundPrimary,
    },
    workContent: {
      textAlign: 'center',
    },
    workTitle: {
      fontSize: FONT_SIZES.xxxl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.md,
      color: COLORS.textPrimary,
    },
    workDescription: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.8',
      marginBottom: SPACING.xxxl,
      maxWidth: '800px',
      margin: `0 auto ${SPACING.xxxl}`,
    },
    workGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: SPACING.lg,
      marginTop: SPACING.xl,
    },
    workCard: {
      ...COMPONENTS.card.base,
      textAlign: 'left',
      backgroundColor: COLORS.backgroundSecondary,
    },
    workCardTitle: {
      fontSize: FONT_SIZES.xl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.base,
      color: COLORS.primary,
    },
    workCardText: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.7',
    },
    adSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundPrimary,
    },
    adGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: SPACING.lg,
      marginTop: SPACING.xl,
    },
    adItem: {
      backgroundColor: COLORS.backgroundSecondary,
      borderRadius: BORDER_RADIUS.md,
      overflow: 'hidden',
      boxShadow: SHADOWS.base,
      transition: TRANSITIONS.base,
      cursor: 'pointer',
    },
    adImage: {
      width: '100%',
      height: 'auto',
      display: 'block',
    },
    contactSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    contactContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    contactInfo: {
      textAlign: 'center',
      backgroundColor: COLORS.backgroundPrimary,
      padding: SPACING.lg,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.base,
    },
    contactInfoItem: {
      marginBottom: SPACING.base,
      fontSize: FONT_SIZES.base,
      color: COLORS.textSecondary,
    },
  };

  return (
    <div>
      <style>
        {MOBILE_STYLES}
        {`
          @media (max-width: 768px) {
            .news-item-mobile {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: ${SPACING.sm} !important;
            }
            .news-date-mobile {
              min-width: auto !important;
            }
            .news-category-mobile {
              width: auto !important;
            }
            .news-title-mobile {
              font-size: ${FONT_SIZES.sm} !important;
            }
          }
        `}
      </style>

      {/* ヒーロースライダー */}
      <HeroSlider slides={heroSliderData} />

      {/* 新着情報セクション */}
      <section style={styles.newsSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">新着情報</h2>
          </AnimatedSection>
          
          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.newsList}>
              {newsData.slice().reverse().slice(0, 3).map((news, index) => (
                <AnimatedSection 
                  key={news.id}
                  animationType="fadeUp" 
                  delay={0.1 * index}
                  duration={0.6}
                >
                  <div 
                    style={styles.newsItem}
                    className="news-item-mobile mobile-spacing-md"
                    onClick={() => navigate(`/news/${news.id}`)}
                    onMouseEnter={(e) => applyCardHover(e)}
                    onMouseLeave={(e) => removeCardHover(e)}
                  >
                    <div style={styles.newsDate} className="news-date-mobile">{news.date}</div>
                    <div style={styles.newsCategory} className="news-category-mobile">{news.category}</div>
                    <div style={styles.newsTitle} className="news-title-mobile">{news.title}</div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.4}>
            <div style={styles.newsButtonContainer}>
              <button onClick={() => navigate('/news')} style={styles.newsButton}>
                新着情報一覧はこちら →
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* WORKセクション */}
      <section id="work" style={styles.workSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">WORK</h2>
          </AnimatedSection>

          <div style={styles.workContent}>
            <AnimatedSection animationType="fadeUp" delay={0.1}>
              <h3 style={styles.workTitle}>旅行手配インバウンド事業</h3>
            </AnimatedSection>

            <AnimatedSection animationType="fadeUp" delay={0.2}>
              <p style={styles.workDescription}>
                私たちは、訪日外国人向けの旅行手配を行っている日本企業です。<br/>
                日本国内海外問わず、旅行会社様の仕入れのお手伝いをいたします。
              </p>
            </AnimatedSection>

            <div style={styles.workGrid} className="mobile-grid-single mobile-gap-md">
              <AnimatedSection animationType="fadeUp" delay={0.1}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <h4 style={styles.workCardTitle}>ホテルの客室ブロック代行</h4>
                  <p style={styles.workCardText}>
                    ホテルの客室の年間ブロック契約を通じて、安定した供給を確保します。
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animationType="fadeUp" delay={0.2}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <h4 style={styles.workCardTitle}>インセンティブ案件手配</h4>
                  <p style={styles.workCardText}>
                    募集ツアー以外にもインセンティブ案件など、ご要望の手配も承ります。
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animationType="fadeUp" delay={0.3}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <h4 style={styles.workCardTitle}>【ホテル様向け】団体送客</h4>
                  <p style={styles.workCardText}>
                    スポット予約やブロック予約など、安定した送客を目指しホテル様と連携します。
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* 広告セクション */}
      <section style={styles.adSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">広告</h2>
          </AnimatedSection>

          <div style={styles.adGrid} className="mobile-grid-single mobile-gap-md">
            {adData.map((ad, index) => (
              <AnimatedSection 
                key={ad.id}
                animationType="fadeUp" 
                delay={0.1 * index}
              >
                <a
                  href={ad.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <div
                    style={styles.adItem}
                    onMouseEnter={(e) => applyAdItemHover(e)}
                    onMouseLeave={(e) => removeAdItemHover(e)}
                  >
                    <img
                      src={ad.imageUrl}
                      alt={ad.name}
                      style={styles.adImage}
                    />
                  </div>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACTセクション */}
      <section id="contact" style={styles.contactSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">CONTACT</h2>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.contactContent}>
              <div style={styles.contactInfo} className="mobile-spacing-md">
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>電話:</strong> 06-6357-3668
                </div>
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>営業時間:</strong> 平日 9:30-18:00
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;